/**
 * Componentes de interface compartilhados — CONTRATO COMPARTILHADO.
 *
 * Todos os painéis (satélite, focos, vetores, exportação) devem usar estes
 * componentes para manter a interface coesa. Pode-se acrescentar novos
 * componentes aqui; não altere as props existentes sem atualizar os usos.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { ChevronDown, X, type LucideIcon } from 'lucide-react';
import { useModalLayer } from './modalStack';
import { readSectionOpen, writeSectionOpen } from './sectionState';

// ---------------------------------------------------------------------------
// Seção recolhível de painel
// ---------------------------------------------------------------------------

export function Section(props: {
  title: string;
  icon?: LucideIcon;
  /** Conteúdo à direita do título (ex.: contador, botão). */
  right?: ReactNode;
  /** Estado inicial na primeira visita; depois vale o que o usuário escolheu (lembrado entre visitas). */
  defaultOpen?: boolean;
  /** Chave para lembrar o estado (padrão: o título). */
  storageKey?: string;
  children: ReactNode;
}) {
  const { title, icon: Icon, right, defaultOpen = true, children } = props;
  const storageKey = props.storageKey ?? title;
  const [open, setOpenState] = useState(() => readSectionOpen(storageKey) ?? defaultOpen);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    writeSectionOpen(storageKey, next);
  };
  const id = useId();
  return (
    <section className="border-b border-ink-700/70 last:border-b-0">
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-semibold text-ink-100 hover:text-white"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen(!open)}
        >
          {Icon && <Icon className="size-4 shrink-0 text-mata-400" aria-hidden />}
          <span className="truncate">{title}</span>
          <ChevronDown
            className={clsx('ml-auto size-4 shrink-0 text-ink-400 transition-transform', !open && '-rotate-90')}
            aria-hidden
          />
        </button>
        {right}
      </div>
      {open && (
        <div id={id} className="space-y-3 px-4 pb-4">
          {children}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Interruptor (toggle)
// ---------------------------------------------------------------------------

/**
 * Só o botão do interruptor (role="switch"). O `Toggle` põe o rótulo ao lado;
 * o cabeçalho do painel usa este sozinho, com `label` como nome acessível.
 */
export function Switch(props: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Nome acessível (aria-label) quando não há <label> apontando para o botão. */
  label?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}) {
  const { checked, onChange, label, id, disabled, className } = props;
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mata-400',
        checked ? 'bg-mata-500' : 'bg-ink-600',
        className,
      )}
    >
      <span
        className={clsx(
          'inline-block size-4 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-4.5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

export function Toggle(props: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  /** Elemento extra à direita (ex.: botão de estilo). */
  right?: ReactNode;
  /** 'sm' = rótulo pequeno, igual ao dos demais controles de um editor. */
  size?: 'sm' | 'md';
}) {
  const { checked, onChange, label, description, disabled, right, size = 'md' } = props;
  const id = useId();
  return (
    <div className={clsx('flex items-start gap-3', disabled && 'opacity-50')}>
      <Switch id={id} checked={checked} onChange={onChange} disabled={disabled} className="mt-0.5" />
      <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer select-none">
        <span className={clsx('block text-ink-100', size === 'sm' ? 'text-xs' : 'text-sm')}>{label}</span>
        {description && <span className="mt-0.5 block text-xs leading-snug text-ink-300">{description}</span>}
      </label>
      {right}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slider
// ---------------------------------------------------------------------------

export function Slider(props: {
  label: ReactNode;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  /** Formata o valor exibido à direita (padrão: o próprio número). */
  format?: (value: number) => string;
  disabled?: boolean;
}) {
  const { label, value, min, max, step = 1, onChange, format, disabled } = props;
  const id = useId();
  return (
    <div className={clsx(disabled && 'opacity-50')}>
      <div className="mb-1 flex items-center justify-between gap-2 text-xs">
        <label htmlFor={id} className="text-ink-300">
          {label}
        </label>
        <span className="font-mono text-ink-200 tabular-nums">{format ? format(value) : value}</span>
      </div>
      <input
        id={id}
        type="range"
        className="w-full"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Select
// ---------------------------------------------------------------------------

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export function Select<T extends string>(props: {
  label?: ReactNode;
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
}) {
  const { label, value, options, onChange, disabled, className } = props;
  const id = useId();
  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="mb-1 block text-xs text-ink-300">
          {label}
        </label>
      )}
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as T)}
        className={clsx(
          'w-full rounded-lg border border-ink-600 bg-ink-800 px-2.5 py-1.5 text-sm text-ink-100',
          'focus:border-mata-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-mata-400/70 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400 disabled:opacity-50',
        )}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Select compacto sem rótulo visível (o rótulo vai em `aria-label`/`title`). */
export function InlineSelect<T extends string>(props: {
  label: string;
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  icon?: LucideIcon;
  disabled?: boolean;
  className?: string;
}) {
  const { label, value, options, onChange, icon: Icon, disabled, className } = props;
  return (
    <div className={clsx('relative inline-flex items-center', className)}>
      {Icon && <Icon className="pointer-events-none absolute left-2 size-3.5 text-ink-400" aria-hidden />}
      <select
        aria-label={label}
        title={label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as T)}
        className={clsx(
          'h-8 w-full cursor-pointer appearance-none rounded-lg border border-ink-600 bg-ink-800 pr-7 text-xs text-ink-100 pointer-coarse:h-11',
          'hover:border-ink-500 focus:border-mata-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-mata-400/70 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400 disabled:opacity-50',
          Icon ? 'pl-7' : 'pl-2.5',
        )}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 size-3.5 text-ink-400" aria-hidden />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grupo de botões segmentado (escolha única compacta)
// ---------------------------------------------------------------------------

export function Segmented<T extends string>(props: {
  label?: ReactNode;
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
}) {
  const { label, value, options, onChange, size = 'md' } = props;
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const labelId = useId();
  // Setas trocam a opção (padrão de radiogroup); só a opção marcada entra no Tab.
  const onKeyDown = (e: ReactKeyboardEvent, i: number) => {
    const delta = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!delta) return;
    e.preventDefault();
    e.stopPropagation();
    const enabled = options.map((o, j) => ({ o, j })).filter(({ o }) => !o.disabled);
    const pos = enabled.findIndex(({ j }) => j === i);
    const next = enabled[(pos + delta + enabled.length) % enabled.length];
    if (!next) return;
    onChange(next.o.value);
    refs.current[next.j]?.focus();
  };
  const selectedIndex = options.findIndex((o) => o.value === value);
  return (
    <div>
      {label && (
        <div id={labelId} className="mb-1 text-xs text-ink-300">
          {label}
        </div>
      )}
      <div
        role="radiogroup"
        aria-labelledby={label ? labelId : undefined}
        className="flex rounded-lg border border-ink-600 bg-ink-800 p-0.5"
      >
        {options.map((o, i) => (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            tabIndex={i === (selectedIndex >= 0 ? selectedIndex : 0) ? 0 : -1}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={clsx(
              'flex-1 rounded-md font-medium whitespace-nowrap transition-colors disabled:opacity-40',
              'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400',
              size === 'sm' ? 'px-1.5 py-1 text-[11px]' : 'px-2 py-1.5 text-xs',
              value === o.value ? 'bg-ink-600 text-white shadow-sm' : 'text-ink-300 hover:text-ink-100',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cor
// ---------------------------------------------------------------------------

export function ColorInput(props: { label: ReactNode; value: string; onChange: (hex: string) => void }) {
  const { label, value, onChange } = props;
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-2">
      <label htmlFor={id} className="text-xs text-ink-300">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] text-ink-300 uppercase">{value}</span>
        <input
          id={id}
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-6 w-8 cursor-pointer rounded border border-ink-600 bg-transparent p-0"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Botões
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button(props: {
  children?: ReactNode;
  onClick?: () => void;
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  icon?: LucideIcon;
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  className?: string;
}) {
  const { children, onClick, variant = 'secondary', size = 'md', icon: Icon, disabled, type = 'button', title, className } = props;
  return (
    <button
      type={type}
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mata-400 disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-2 text-sm',
        variant === 'primary' && 'bg-mata-500 text-ink-950 hover:bg-mata-400',
        variant === 'secondary' && 'border border-ink-600 bg-ink-800 text-ink-100 hover:bg-ink-700',
        variant === 'ghost' && 'text-ink-200 hover:bg-ink-700/70 hover:text-white',
        variant === 'danger' && 'bg-brasa-600 text-white hover:bg-red-500',
        className,
      )}
    >
      {Icon && <Icon className={size === 'sm' ? 'size-3.5' : 'size-4'} aria-hidden />}
      {children}
    </button>
  );
}

export function IconButton(props: {
  icon: LucideIcon;
  /** Texto acessível e dica (title). */
  label: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const { icon: Icon, label, onClick, active, disabled, size = 'md', className } = props;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded-lg transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mata-400 disabled:cursor-not-allowed disabled:opacity-40',
        size === 'sm' && 'size-7',
        size === 'md' && 'size-9',
        size === 'lg' && 'size-11',
        active ? 'bg-mata-500/20 text-mata-300' : 'text-ink-200 hover:bg-ink-700/70 hover:text-white',
        className,
      )}
    >
      <Icon className={size === 'lg' ? 'size-6' : size === 'sm' ? 'size-4' : 'size-5'} aria-hidden />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Diálogo modal
// ---------------------------------------------------------------------------

export function Dialog(props: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Largura máxima (classe Tailwind). Padrão: max-w-lg. */
  widthClass?: string;
  footer?: ReactNode;
}) {
  const { open, onClose, title, children, widthClass = 'max-w-lg', footer } = props;
  const ref = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Pilha de camadas: Escape fecha só o diálogo de cima; o resto fica `inert`
  // (o Tab não sai do diálogo) e o foco volta ao fechar.
  useModalLayer(open, { onEscape: onClose, ref: overlayRef });

  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);

  if (!open) return null;
  // Portal para o <body>: painéis "glass" usam backdrop-filter, que prenderia
  // um elemento `fixed` dentro deles.
  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex items-end justify-center bg-ink-950/70 p-0 sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={clsx(
          'glass flex max-h-[92dvh] w-full flex-col rounded-t-2xl shadow-2xl outline-none sm:rounded-2xl',
          widthClass,
        )}
      >
        <header className="flex items-center gap-2 border-b border-ink-700/70 px-5 py-3.5">
          <h2 className="flex-1 text-base font-semibold text-white">{title}</h2>
          <IconButton icon={X} label="Fechar" onClick={onClose} size="sm" />
        </header>
        <div className="scroll-thin flex-1 overflow-y-auto px-5 py-4 text-sm text-ink-200">{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t border-ink-700/70 px-5 py-3">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Pequenos auxiliares visuais
// ---------------------------------------------------------------------------

export function Badge(props: { children: ReactNode; tone?: 'neutral' | 'mata' | 'fogo' | 'rio' | 'alerta' }) {
  const { children, tone = 'neutral' } = props;
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
        tone === 'neutral' && 'bg-ink-700 text-ink-200',
        tone === 'mata' && 'bg-mata-500/15 text-mata-300',
        tone === 'fogo' && 'bg-fogo-500/15 text-fogo-400',
        tone === 'rio' && 'bg-rio-400/15 text-rio-400',
        tone === 'alerta' && 'bg-yellow-400/15 text-yellow-300',
      )}
    >
      {children}
    </span>
  );
}

/** Barra de progresso fina (0–1). */
export function ProgressBar(props: { value: number; tone?: 'mata' | 'fogo'; className?: string }) {
  const { value, tone = 'mata', className } = props;
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className={clsx('h-1 w-full overflow-hidden rounded-full bg-ink-700', className)}>
      <div
        className={clsx('h-full rounded-full transition-[width] duration-200', tone === 'mata' ? 'bg-mata-400' : 'bg-fogo-500')}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** Texto de ajuda discreto. */
export function Hint(props: { children: ReactNode }) {
  return <p className="text-xs leading-relaxed text-ink-300">{props.children}</p>;
}
